import { match } from '@frontend/duck-tape';
import type { MouseEvent } from '@frontend/react';
import type { TextColorVariant } from '@frontend/web-react';
import { Text } from '@frontend/web-react';
import { Spinner } from '@frontend/web-react/components/Primitives/Spinner';
import type { ClassName } from '@frontend/web-utils';
import { concatClasses, safeCss } from '@frontend/web-utils';
import { Button as MButton } from '@mantine/core';
import { useMemo } from 'react';

type ButtonVariant = 'filled' | 'outlined' | 'subtle';

type ButtonProps = {
  className?: ClassName;
  disabled?: boolean;
  isLoading?: boolean;
  label: string;
  onClick?: (e?: MouseEvent<HTMLButtonElement>) => void;
  textClassName?: ClassName;
  variant: ButtonVariant;
};

type VariantStyle = { color: TextColorVariant; containerClassName: ClassName };

const getVariantClasses = (variant: ButtonVariant): VariantStyle =>
  match<ButtonVariant, VariantStyle>(variant)
    .with('filled', () => ({
      color: 'primary',
      containerClassName: safeCss('bg-surfaceAccentPrimary border-1'),
    }))
    .with('outlined', () => ({
      color: 'primary',
      containerClassName: safeCss('bg-buttonSecondary border-1 border-borderPrimary shadow-trueWhite'),
    }))
    .with('subtle', () => ({
      color: 'secondary',
      containerClassName: safeCss('bg-transparent border-transparent'),
    }))
    .exhaustive();

export const CardButton = (props: ButtonProps) => {
  const { className, disabled, isLoading, label, onClick, textClassName, variant } = props;

  const { color, containerClassName } = useMemo(() => getVariantClasses(variant), [variant]);

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onClick?.(e);
  };

  return (
    <MButton
      className={concatClasses(containerClassName, 'rounded-md border-1 px-sm py-xs', className)}
      disabled={disabled || isLoading}
      onClick={handleClick}
    >
      {isLoading ? (
        <Spinner size="sm" />
      ) : (
        <Text className={textClassName} color={color} type="h5">
          {label}
        </Text>
      )}
    </MButton>
  );
};
